import React from "react";
import { Building2, Tag, Info } from "lucide-react";

const peso = (n) => `₱${Number(n || 0).toLocaleString()}`;
const pesoDecimal = (n) => `₱${Number(n || 0).toLocaleString("en-PH", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

/**
 * Phase 1 public presentation for optional development attribution + Payment & Promo.
 * - Promo shown only for active (non-expired) For-Sale promos; expired promos are de-emphasized.
 * - NO computed monthly DP / DP amount (professional-supplied inputs only).
 * - Attribution is neutral (never implies PropertyConnect endorsement/partnership).
 */
export const ListingCommercialDetails = ({ p }) => {
  if (!p) return null;
  const pp = p.payment_promo || null;
  const price = Number(p.price || 0);
  const isSale = p.listing_type === "For Sale";
  // Deterministic project display precedence: structured project_name (canonical/candidate) -> legacy free-text `project`.
  const displayProject = p.project_name || p.project || "";
  const hasDev = p.developer_name || displayProject;

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const validUntil = pp?.promo_valid_until ? new Date(pp.promo_valid_until) : null;
  const promoExpired = !!(pp?.promo_price && validUntil && validUntil < today);
  const promoActive = !!(isSale && pp?.promo_price && pp.promo_price <= price && !promoExpired);
  const discountAmt = promoActive ? price - pp.promo_price : 0;
  const discountPct = discountAmt > 0 && price > 0 ? Math.round((discountAmt / price) * 1000) / 10 : 0;

  const hasPayment = isSale && pp && (pp.reservation_fee != null || pp.monthly_downpayment_amount != null || pp.down_payment_percent != null || pp.dp_term_months != null || pp.terms);
  if (!hasDev && !promoActive && !promoExpired && !hasPayment) return null;

  const fmtDate = (d) => d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

  return (
    <div className="mt-4 space-y-3" data-testid="commercial-details">
      {promoActive && (
        <div className="rounded-xl border border-[#C79A3B]/40 bg-[#C79A3B]/5 px-4 py-3" data-testid="promo-active">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-[#8a6a1f]"><Tag className="h-3.5 w-3.5" /> Promo</div>
          <div className="font-display text-2xl font-bold text-slate-900 mt-1">{peso(pp.promo_price)}</div>
          {discountAmt > 0 && <div className="text-sm font-semibold text-emerald-700 mt-0.5">Save {peso(discountAmt)} ({discountPct}%)</div>}
          {validUntil && <div className="text-xs text-slate-500 mt-0.5">Valid until {fmtDate(validUntil)}</div>}
        </div>
      )}
      {promoExpired && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-xs text-slate-500" data-testid="promo-expired">
          A promo of {peso(pp.promo_price)} was listed but may be outdated — confirm current pricing with the listing professional.
        </div>
      )}
      {hasPayment && (
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-3" data-testid="payment-details">
          <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Payment Details</div>
          <div className="mt-1.5 space-y-1 text-sm text-slate-700">
            {pp.reservation_fee != null && <div>Reservation fee: <b>{peso(pp.reservation_fee)}</b></div>}
            {pp.monthly_downpayment_amount != null && <div data-testid="monthly-downpayment">Monthly Downpayment: <b>{pesoDecimal(pp.monthly_downpayment_amount)}</b></div>}
            {pp.monthly_downpayment_amount == null && pp.down_payment_percent != null && <div>Down payment: <b>{pp.down_payment_percent}%</b></div>}
            {pp.dp_term_months != null && <div>DP term: <b>{pp.dp_term_months} month(s)</b></div>}
            {pp.terms && <div className="text-slate-500 text-xs pt-1">{pp.terms}</div>}
          </div>
        </div>
      )}
      {(promoActive || promoExpired || hasPayment) && (
        <p className="text-[11px] text-slate-400 flex items-center gap-1"><Info className="h-3 w-3" /> Payment and promotional details are provided by the listing professional. Please verify current terms and availability with the property representative.</p>
      )}
      {hasDev && (
        <div className="flex flex-wrap gap-2 pt-1" data-testid="development-attribution">
          {p.developer_name && <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-600"><Building2 className="h-3 w-3 text-slate-400" /> Developer: <b className="text-slate-800 font-semibold">{p.developer_name}</b></span>}
          {displayProject && <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-600">Development: <b className="text-slate-800 font-semibold">{displayProject}</b></span>}
        </div>
      )}
    </div>
  );
};
